import { TreeSelect } from 'antd'
import { TreeSelectProps } from 'antd/lib'
import { useEffect, useState } from 'react'

import { useClassifierServiceGetCuatmClassifierElementListEndpoint } from 'src/api/queries'
import { ClassifierCategory, ClassifierType } from 'src/api/requests'
import { DEFAULT_ONE_HUNDRED_PER_PAGE } from 'src/app-constants'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'
import { getLocalizedTitle } from 'src/utils'

type TreeItemType = {
  id?: string
  value?: string
  title?: string
  isLeaf: boolean
  pId?: string
  selectable?: boolean
}

type Props = {
  elementId?: string
} & TreeSelectProps

export const CUATMTreeSelect = ({ elementId, ...props }: Props) => {
  const [treeData, setTreeData] = useState<TreeItemType[]>([])
  const [parentId, setParentId] = useState<string>()
  const { t, currentLang } = useLocales()

  const cuatmClassifierId = useClassifierId(
    ClassifierType.CUATM,
    ClassifierCategory.DIAGNOSTIC
  )

  const { data, isLoading, isFetching, isError } =
    useClassifierServiceGetCuatmClassifierElementListEndpoint(
      {
        id: String(cuatmClassifierId),
        pageNumber: 1,
        pageSize: DEFAULT_ONE_HUNDRED_PER_PAGE,
        hasParent: !!parentId,
        parentClassifierElementId: parentId
      },
      [parentId, cuatmClassifierId, String(elementId)],
      {
        enabled: !!cuatmClassifierId,
        staleTime: 1000 * 60 * 5
      }
    )

  useEffect(() => {
    if (data) {
      const transformedTreeData = (data?.data ?? [])
        .map((item) => ({
          id: item.id,
          value: item.id,
          title: getLocalizedTitle({
            item,
            t,
            currentLang: currentLang.value
          }),
          isLeaf: !item.hasChildren,
          pId: parentId,
          selectable: !!item.parentCode
        }))
        .toSorted((a, b) =>
          (a.title ?? '').localeCompare(b.title ?? '', currentLang.value)
        )

      setTreeData((prev) => [...prev, ...transformedTreeData])
    }
  }, [data])

  useEffect(() => {
    if (elementId) {
      setParentId(elementId)
      setTreeData([])
    }
  }, [elementId])

  const onLoadData: TreeSelectProps['loadData'] = async ({ id }) => {
    setParentId(id)
  }

  return (
    <TreeSelect
      treeDataSimpleMode
      allowClear
      showSearch
      treeNodeFilterProp="title"
      treeData={treeData}
      loadData={onLoadData}
      loading={isLoading || isFetching}
      disabled={isError}
      {...props}
    />
  )
}
